/**
 * The Trash section of the sidebar (`060-notebook-tree.md` §5, §4).
 *
 * Collapsed by default and **fetched lazily on first expand** — the archived tree is a
 * second full query, and most sessions never look at it.
 *
 * Deletion is always two steps: archive, then delete from the trash. There is deliberately
 * no "delete permanently" button anywhere on a live page, and `DELETE` on a non-archived
 * page is a `409` server-side.
 */
import { useState } from 'react';
import { NavLink } from 'react-router';
import { ChevronRight, RotateCcw, Trash2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import type { TreePage } from '@/api/endpoints/pages';
import { useDeletePage, useRestorePage, useTrash } from '@/features/notebook/usePageMutations';

export function Trash() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<TreePage | null>(null);

  const trash = useTrash(open);
  const restore = useRestorePage();
  const remove = useDeletePage();

  const pages = trash.data?.pages ?? [];

  // Rendered as a tree of ARCHIVED ROOTS (§5). A page whose parent is *also* archived came
  // along in the same cascade — restoring it separately is not a thing anyone means to do,
  // and listing it would just be the same subtree three times over.
  const archived = new Set(pages.map((page) => page.id));
  const roots = pages.filter((page) => page.parentId === null || !archived.has(page.parentId));

  return (
    <div className="group/trash shrink-0 border-t border-[var(--border)] pt-1">
      <div className="flex h-[var(--row-h)] items-center pr-1 text-[var(--muted-foreground)] hover:bg-[var(--accent)]">
        <button
          type="button"
          data-testid="trash-toggle"
          aria-expanded={open}
          onClick={() => {
            setOpen((previous) => !previous);
          }}
          className="flex h-full min-w-0 flex-1 items-center gap-1.5 px-[var(--pad-x)]"
        >
          <ChevronRight className={cn('size-3 transition-transform', open && 'rotate-90')} />
          <Trash2 className="size-[var(--icon)]" strokeWidth={1.75} />
          <span className="text-xs font-medium tracking-wide uppercase">
            {t('notebook.trash.title')}
          </span>
        </button>

        {/* The peek is the roots and two buttons; the ROOM is `/trash` — every archived page with
            its children, and "empty trash" (`060` §5, M7). `G` then `T` goes there too. */}
        <NavLink
          to="/trash"
          title={t('notebook.trash.openTitle')}
          className="rounded-sm px-1 text-2xs underline-offset-2 opacity-0 group-hover/trash:opacity-100 focus-visible:opacity-100 hover:underline"
        >
          {t('notebook.trash.open')}
        </NavLink>
      </div>

      {open && (
        <div data-testid="trash-list" className="max-h-48 overflow-y-auto px-1 pb-1">
          {trash.isPending ? (
            <p className="px-[var(--pad-x)] py-1 text-[var(--muted-foreground)]">
              {t('common.loading')}
            </p>
          ) : roots.length === 0 ? (
            <p className="px-[var(--pad-x)] py-1 text-[var(--muted-foreground)]">
              {t('notebook.trash.empty')}
            </p>
          ) : (
            roots.map((page) => (
              <div
                key={page.id}
                data-testid={`trash-row-${page.id}`}
                className="group flex h-[var(--row-h)] items-center gap-1 rounded-sm px-[var(--pad-x)] hover:bg-[var(--accent)]"
              >
                <span className="min-w-0 flex-1 truncate" title={page.title}>
                  {page.title}
                </span>

                <span className="flex shrink-0 items-center opacity-0 group-hover:opacity-100">
                  <button
                    type="button"
                    aria-label={t('notebook.trash.restoreAriaLabel', { title: page.title })}
                    title={t('notebook.trash.restoreTitle')}
                    onClick={() => {
                      restore.mutate(page.id);
                      toast.success(t('notebook.trash.restoringToast', { title: page.title }));
                    }}
                    className="flex size-5 items-center justify-center rounded-sm hover:bg-[var(--border)]"
                  >
                    <RotateCcw className="size-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    aria-label={t('notebook.trash.deletePermanentlyAriaLabel', {
                      title: page.title,
                    })}
                    title={t('notebook.trash.deletePermanentlyTitle')}
                    onClick={() => {
                      setConfirm(page);
                    }}
                    className="flex size-5 items-center justify-center rounded-sm text-[var(--destructive)] hover:bg-[var(--border)]"
                  >
                    <X className="size-3.5" strokeWidth={2} />
                  </button>
                </span>
              </div>
            ))
          )}
        </div>
      )}

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(next) => {
          if (!next) setConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('notebook.trash.deleteConfirmTitle', { title: confirm?.title ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('notebook.trash.deleteConfirmBodySimple')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = confirm;
                setConfirm(null);
                if (target === null) return;
                remove.mutate(target.id);
                toast.success(t('notebook.trash.deletedToast', { title: target.title }));
              }}
            >
              {t('notebook.trash.deletePermanentlyTitle')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
